<template lang="pug">
//- Ending a session means deciding where the time goes: onto a ticket, into
//- the bin, or - said out loud - nowhere yet, the clock keeps running. That
//- last answer used to be refused when leaving a customer's account, because
//- a forgotten timer counted hours nobody had decided about; the header pill
//- now shows a running timer wherever you are, so keeping it running is a
//- choice a person can see, not a row they lose track of.
el-dialog.stop-support-time-dialog(
    :title="$t('Record effort')"
    :visible="dialog.visible"
    width="480px"
    :close-on-click-modal="false"
    :close-on-press-escape="dismissible"
    :show-close="dismissible"
    @close="cancel"
)
    template(v-if="stash")
        //- Which ticket the time lands on, stated before anything is entered.
        //- The subject is editable here: at the START of a session it is a guess
        //- typed in a hurry, and only now is it clear what the call was about.
        //- Gone once the time is booked elsewhere: the subject would be silently
        //- dropped (stop() skips the rename when movedAway), and the number
        //- beside it would name the draft that is about to be deleted. What the
        //- time lands on is then what "Book the time on" says, and only that.
        .tracked-ticket(v-if="!movedAway")
            span.tracked-ticket__number {{ stash.ticketNumber }}
            el-input.tracked-ticket__subject-input(
                v-model="subject"
                size="small"
                :placeholder="$t('What was it about?')"
            )
        el-form.stop-form(label-position="top" size="small" @submit.native.prevent)
            el-form-item(:label="$t('Duration')")
                duration-input(v-model="durationMinutes")
                p.field-hint {{ $t('Billed per started {block} minutes.', { block: blockMinutes }) }}
                //- Why the number is smaller than the wall time: a pause is
                //- not work, and it is not on the bill.
                p.field-hint(v-if="pausedMinutes") {{ $t('{minutes} min. of pause are not counted.', { minutes: pausedMinutes }) }}
            el-form-item(:label="$t('Note')")
                el-input(v-model="note" type="textarea" :rows="2" :placeholder="$t('What was done? Appears on the billing breakdown.')")
            //- A call that turned out to be about something already open belongs
            //- on THAT ticket. Booking it here beats a session ticket nobody
            //- ever links up, and the empty one left behind is removed.
            el-form-item(:label="$t('Book the time on')")
                el-select(
                    v-model="targetTicketId"
                    filterable
                    style="width: 100%"
                    :loading="loadingTickets"
                    popper-class="ticket-option-dropdown"
                    @visible-change="onTicketDropdown"
                )
                    el-option(
                        :label="stash.ticketNumber + ' · ' + ticketSubject(subject, $t('No subject'))"
                        :value="stash.ticketId"
                    )
                    el-option(
                        v-for="ticket in otherTickets"
                        :key="ticket.id"
                        :label="ticket.number + ' · ' + ticketSubject(ticket.subject, $t('No subject'))"
                        :value="ticket.id"
                    )
                        ticket-option(:ticket="ticket")
            //- Phone support can solve a ticket on the spot, and the status is
            //- then set where the work actually ended.
            el-form-item(:label="$t('Status when stopping')")
                el-select(v-model="status" clearable style="width: 100%" :placeholder="$t('Leave unchanged')")
                    el-option(
                        v-for="option in statuses"
                        :key="option"
                        :value="option"
                        :label="$t('ticket_status.' + option)"
                    )
            //- The other half of "where does this leave the ticket". Same
            //- control the ticket detail uses, so a number set here and a
            //- number set there are the same gesture.
            el-form-item(v-if="progressLoaded" :label="$t('Progress')")
                el-slider(
                    v-model="progress"
                    :step="5"
                    show-stops
                    :format-tooltip="formatProgress"
                )
            //- Only our own desk bills: the synchronizer counts `desk = platform`,
            //- so the question is not asked on a shop's own helpdesk - the same
            //- gate the ticket's time panel and `Zeit nachtragen` apply.
            el-form-item(v-if="isPlatform")
                billable-state-select(v-model="billable")

    //- Gated with the body: clearing the stash on confirm empties the dialog
    //- while it is still closing, and a footer with nothing above it flashes.
    //- Throwing the time away is not a peer of the two ways to keep it, so it
    //- sits alone at the far left — the cancel-purchase dialog's own footer.
    template(slot="footer" v-if="stash")
        el-button(
            v-if="canDeleteTickets"
            size="mini"
            type="danger"
            icon="el-icon-fa-trash-alt far"
            :disabled="stopping"
            @click="discard"
        ) {{ $t('Discard time') }}
        el-button(
            v-if="dismissible"
            size="mini"
            icon="el-icon-fa-times fas"
            :disabled="stopping"
            @click="cancel"
        ) {{ $t('Keep running') }}
        el-button(
            size="mini"
            type="primary"
            icon="el-icon-fa-check fas"
            :loading="stopping"
            @click="confirmWithDetails"
        ) {{ $t('Apply time') }}
</template>

<i18n>
{
    "en": {
        "{minutes} min. of pause are not counted.": "{minutes} min. of pause are not counted.",
        "Billed per started {block} minutes.": "Billed per started {block} minutes.",
        "Progress": "Progress"
    },
    "de": {
        "{minutes} min. of pause are not counted.": "{minutes} Min. Pause werden nicht berechnet.",
        "Record effort": "Aufwand erfassen",
        "Billed per started {block} minutes.": "Wird je angefangene {block} Minuten berechnet.",
        "Duration": "Dauer",
        "Note": "Notiz",
        "What was it about?": "Worum ging es?",
        "What was done? Appears on the billing breakdown.": "Was wurde gemacht? Erscheint in der Abrechnungsübersicht.",
        "Book the time on": "Zeit buchen auf",
        "No subject": "Ohne Betreff",
        "Status when stopping": "Status beim Beenden",
        "Leave unchanged": "Unverändert lassen",
        "Progress": "Fortschritt",
        "Discard time": "Zeit verwerfen",
        "Keep running": "Weiterlaufen lassen",
        "Apply time": "Zeit erfassen",
        "The time entry could not be saved. It stays running and can be closed in the ticket area.": "Der Zeiteintrag konnte nicht gespeichert werden. Er läuft weiter und kann im Ticketbereich abgeschlossen werden."
    }
}
</i18n>

<script>
/**
 * Ending a session: elapsed time (editable), note, where the time is booked,
 * the ticket's status afterwards, and whether the customer pays for it.
 *
 * Both desks use this, so every call carries the desk's token — our own session
 * runs on our own token even while the browser sits inside a customer's
 * account, where the mimic already ended by the time this confirms.
 *
 * The time goes one of two ways: onto a ticket, or into the bin. There used to
 * be a "decide later" that left the entry RUNNING, which kept counting hours
 * after the agent had left the customer's account; booking it silently instead
 * would have been just as wrong, since nobody decided whether the customer pays
 * for it. Only a failed request leaves the entry running, and the ticket area
 * can close it.
 */
import _ from 'lodash';

import { useTicketStore } from '@/stores';
import { checkRestrictions } from '@/helpers';
import { activeSeconds, pausedSecondsSoFar } from '@/helpers/duration';

import { ticketSubject } from '@/filters';
import TicketApi from '@/extensions/tickets/store/api/ticket';
import DurationInput from '@/components/DurationInput';
import BillableStateSelect from '@/extensions/tickets/components/BillableStateSelect';
import TicketOption from '@/extensions/tickets/components/TicketOption';
import { TICKET_CHOOSABLE_STATUS_KEYS, TICKET_OPEN_STATUS_KEYS } from '@/extensions/tickets/ticketStatus';


export default {
    name: 'StopSupportTimeDialog',
    components: {
        'ticket-option': TicketOption,
        'duration-input': DurationInput,
        'billable-state-select': BillableStateSelect
    },
    data() {
        return {
            durationMinutes: 0,
            pausedMinutes: 0,
            subject: '',
            note: '',
            billable: true,
            targetTicketId: null,
            status: null,
            // Loaded from the ticket the time will land on: a slider that opens
            // at zero would quietly reset a ticket that is at 60.
            progress: 0,
            savedProgress: 0,
            progressLoaded: false,
            otherTickets: [],
            loadingTickets: false,
            stopping: false
        };
    },
    computed: {
        ticketStore() {
            return useTicketStore();
        },
        // Throwing the time away deletes the entry, the delete flag.
        canDeleteTickets() {
            return checkRestrictions(['ex:tckt:tickets'], 'delete');
        },
        dialog() {
            return this.ticketStore.stopDialog;
        },
        // The time is being booked onto a ticket other than the session's own.
        // The draft this session opened is then deleted by the stop mutation
        // (deleteAbandonedDraft), so anything that names it has nothing to say.
        movedAway() {
            return !!this.targetTicketId && this.targetTicketId !== _.get(this.stash, 'ticketId');
        },
        // Which desk is being stopped. Leaving an account stops ours; the header
        // stops whichever pill was clicked.
        desk() {
            return this.dialog.desk || 'platform';
        },
        isPlatform() {
            return this.desk === 'platform';
        },
        // Leaving the account is not a question; a header stop is.
        dismissible() {
            return !!this.dialog.dismissible;
        },
        stash() {
            return this.isPlatform ? this.ticketStore.platformTrackingStash : this.ticketStore.trackingStash;
        },
        // closed is system-set by the status timers, so it is not on offer here
        // any more than it is on the ticket form.
        statuses() {
            return TICKET_CHOOSABLE_STATUS_KEYS;
        },
        // The billing block size the duration is rounded up to.
        blockMinutes() {
            return 15;
        }
    },
    watch: {
        'dialog.visible'(visible) {
            if (!visible || !this.stash) return;

            // The active time, not the wall time: a pause does not count.
            const elapsedSeconds = activeSeconds(this.stash);
            this.pausedMinutes = Math.round(pausedSecondsSoFar(this.stash) / 60);
            this.durationMinutes = Math.max(1, Math.ceil(elapsedSeconds / 60));
            this.subject = this.stash.subject || '';
            this.note = '';
            this.billable = true;
            this.status = null;
            this.targetTicketId = this.stash.ticketId;
            this.otherTickets = [];
            this.loadProgress(this.stash.ticketId);
        },
        // Booking onto a different ticket means a different progress.
        targetTicketId(id) {
            if (this.dialog.visible) this.loadProgress(id);
        },
        // Completed IS done, so the bar follows the status rather than being left
        // behind at whatever it was. The server enforces the same rule; this is
        // so the number moves while the dialog is still open.
        status(value) {
            if (value === 'completed') this.progress = 100;
        }
    },
    methods: {
        ticketSubject,
        formatProgress(value) {
            return `${value} %`;
        },
        loadProgress(ticketId) {
            this.progressLoaded = false;

            if (!ticketId) return Promise.resolve();

            return TicketApi.fetch(ticketId)
                .then((ticket) => {
                    const value = parseInt(_.get(ticket, 'progress'), 10) || 0;
                    this.progress = value;
                    this.savedProgress = value;
                    this.progressLoaded = true;
                })
                .catch(() => {
                    // Not worth failing the stop over - the slider simply stays
                    // out of the way and the progress is left as it was.
                    this.progressLoaded = false;
                });
        },
        // Called off: the clock keeps running, which is the only reason to offer
        // a way out of this dialog at all. A session timer kept running past
        // LEAVING the account is adopted as an ordinary one: the next mimic,
        // related or not, must not ask about it again on the way out. Only
        // then - called off from the header while still inside the account,
        // it stays the session's timer and is asked about on the way out.
        cancel() {
            if (this.stopping) return;

            if (this.dialog.leaving && this.stash && this.stash.mimic) {
                this.ticketStore.writeTrackingStash(this.desk, { ...this.stash, mimic: false });
            }

            this.ticketStore.closeStopDialog();
        },
        // Loaded when the list is first opened rather than on every stop: most
        // sessions are booked on the ticket they started on.
        onTicketDropdown(open) {
            if (!open || this.otherTickets.length || this.loadingTickets) return;

            this.loadingTickets = true;
            const filters = [{ key: 'status', in: TICKET_OPEN_STATUS_KEYS }];
            if (this.stash.cmsId) filters.push({ key: 'cms', match: this.stash.cmsId });

            TicketApi.fetchPaginated({
                currentPage: 1,
                perPage: 50,
                order: 'created_at',
                direction: 'desc',
                link: '',
                desk: this.desk,
                filters
            })
                .then((data) => {
                    this.otherTickets = (data.items || []).filter((ticket) => ticket.id !== this.stash.ticketId);
                })
                .catch(() => {
                    this.otherTickets = [];
                })
                .finally(() => {
                    this.loadingTickets = false;
                });
        },
        // Nothing to book: the session is thrown away with its entry, so no
        // half-decided row is left for someone to find later - and the
        // ticket the timer opened for it goes too (the server decides, see
        // Ticket::deleteAbandonedDraft).
        async discard() {
            if (this.stopping) return;

            const ticketId = _.get(this.stash, 'ticketId');

            this.stopping = true;
            try {
                await TicketApi.deleteTimeEntry(this.stash.entryId);

                this.ticketStore.clearTrackingStash(this.desk);
                this.ticketStore.closeStopDialog();
                // The ticket's own time list is showing this session; tell it.
                this.ticketStore.sessionChanged();
                await this.leaveIfGone(ticketId);
            } catch (error) {
                /* eslint no-console: ["error", { allow: ["error"] }] */
                console.error(error);
                this.notifyFailure();
            } finally {
                this.stopping = false;
            }
        },
        confirmWithDetails() {
            return this.stop({ note: this.note.trim() || undefined, billable: this.billable });
        },
        async stop({ note, billable }) {
            if (this.stopping) return;

            const movedAway = this.movedAway;
            // Read now: clearing the stash below empties `this.stash`, and the
            // page to leave is the one the time came FROM.
            const originTicketId = this.stash.ticketId;

            this.stopping = true;
            try {
                // Save a corrected subject first: if stopping then fails, the
                // entry stays running but the ticket is at least named right.
                // Not when the time is moving away — that would name a ticket
                // that is about to be discarded, or rename the wrong one.
                const subject = this.subject.trim();
                if (!movedAway && subject !== (this.stash.subject || '')) {
                    await TicketApi.update(this.stash.ticketId, { subject: subject || null });
                }

                await TicketApi.stopSupportTimeEntry(
                    {
                        id: this.stash.entryId,
                        durationSeconds: this.durationMinutes * 60,
                        note,
                        billable,
                        // The server moves the entry, re-bills the customer it
                        // came from, and removes the draft it leaves behind.
                        ticketId: movedAway ? this.targetTicketId : undefined,
                        status: this.status || undefined
                    }
                );

                // Only when it actually moved, and onto the ticket the time
                // landed on rather than the draft the move leaves behind.
                if (this.progressLoaded && this.progress !== this.savedProgress) {
                    const progressTicketId = movedAway ? this.targetTicketId : this.stash.ticketId;
                    await TicketApi.update(progressTicketId, { progress: this.progress });
                }

                this.ticketStore.clearTrackingStash(this.desk);
                this.ticketStore.closeStopDialog();
                this.ticketStore.sessionChanged();
                if (movedAway) await this.leaveIfGone(originTicketId);
            } catch (error) {
                /* eslint no-console: ["error", { allow: ["error"] }] */
                console.error(error);
                this.notifyFailure(_.get(error, 'message'));
            } finally {
                this.stopping = false;
            }
        },
        // Standing on the page of the ticket the timer opened while its time
        // is discarded or moved away: the server may just have deleted that
        // ticket, and the page would refetch it into "not available here".
        // Asked rather than assumed - a placeholder that gained a message is
        // kept, and then there is nothing to leave.
        async leaveIfGone(ticketId) {
            if (!ticketId || String(_.get(this.$route, 'params.id')) !== String(ticketId)) return;

            try {
                await TicketApi.fetch(ticketId);
            } catch (error) {
                this.$router.push({ name: this.isPlatform ? 'system-tickets' : 'tickets' });
            }
        },
        // Whatever failed, the entry is still running and the ticket area can
        // close it — so say that instead of leaving the agent guessing.
        notifyFailure(message = null) {
            this.ticketStore.closeStopDialog();
            this.$notify.error({
                title: this.$t('An error occurred!'),
                message:
                    message ||
                    this.$t(
                        'The time entry could not be saved. It stays running and can be closed in the ticket area.'
                    ),
                iconClass: 'fas el-icon-fa-xmark',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            });
        }
    }
};
</script>

<style lang="sass" scoped>
.tracked-ticket
    display: flex
    align-items: baseline
    gap: 0.5rem
    margin-bottom: 1rem
    padding: 0.625rem 0.75rem
    background: #f5f7fa
    border-radius: 6px

    .tracked-ticket__number
        font-weight: 600
        color: #303133
        white-space: nowrap

    .tracked-ticket__subject
        color: #606266
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap

    // An ordinary field, not a label pretending to be one: without a border
    // nothing says the name can be corrected here, which is the whole point.
    .tracked-ticket__subject-input
        flex: 1
        min-width: 0

.stop-form
    ::v-deep .el-form-item
        margin-bottom: 1rem

        &:last-child
            margin-bottom: 0

// The cancel-purchase dialog's footer, verbatim: the odd one out is pushed to
// the far left, the rest keep the framework's own spacing on the right.
.stop-support-time-dialog::v-deep .el-dialog__footer
    display: flex
    align-items: center

    .el-button:first-child
        margin-right: auto

.field-hint
    margin: 0.25rem 0 0
    font-size: 0.75rem
    line-height: 1.4
    color: #909399
</style>
